import {
  ArrowTopRightOnSquareIcon,
  BuildingStorefrontIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Chip,
  ConfirmDialog,
  DataTable,
  LinkButton,
  notify,
  PageHeader,
} from "../../components/ui";
import type { Column } from "../../components/ui";
import { useApiError } from "../../hooks/useApiError";
import { usePageTitle } from "../../hooks/usePageTitle";
import api from "../../services/api";
import type { User } from "../../types/User";
import type { LocationRecord, RestaurantRecord } from "../Locations/apiTypes";
import { useRequest } from "../Locations/useRequest";
import { Logo } from "./Logo";

interface Row extends RestaurantRecord {
  locationCount: number;
  teamCount: number;
}

const RestaurantList = () => {
  const { t } = useTranslation(["locations", "common"]);
  usePageTitle(t("restaurants.list.title"));
  const apiError = useApiError();
  const [pendingDelete, setPendingDelete] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data, state, retry, reload } = useRequest("restaurants", async () => {
    const [restaurants, locations, users] = await Promise.all([
      api.get<RestaurantRecord[]>("/restaurants").then((r) => r.data),
      api.get<LocationRecord[]>("/locations").then((r) => r.data),
      api.get<User[]>("/users").then((r) => r.data),
    ]);
    return restaurants.map((r): Row => ({
      ...r,
      locationCount: locations.filter((l) => l.restaurantId === r.id).length,
      teamCount: users.filter((u) => u.restaurant?.id === r.id).length,
    }));
  });

  const columns: Column<Row>[] = [
    {
      id: "name",
      header: t("restaurants.list.columns.name"),
      priority: "primary",
      sortValue: (r) => r.name,
      cell: (r) => (
        <span className="inline-flex items-center gap-3">
          <Logo url={r.logoUrl} name={r.name} className="size-8" />
          {r.name}
        </span>
      ),
    },
    {
      id: "plan",
      header: t("restaurants.list.columns.plan"),
      priority: "secondary",
      sortValue: (r) => r.plan,
      cell: (r) => <Chip variant="info">{t(`common:plans.${r.plan}`)}</Chip>,
    },
    {
      id: "locations",
      header: t("restaurants.list.columns.locations"),
      priority: "meta",
      align: "end",
      sortValue: (r) => r.locationCount,
      cell: (r) => r.locationCount,
    },
    {
      id: "team",
      header: t("restaurants.list.columns.team"),
      priority: "meta",
      align: "end",
      sortValue: (r) => r.teamCount,
      cell: (r) => r.teamCount,
    },
    {
      id: "languages",
      header: t("restaurants.list.columns.languages"),
      priority: "meta",
      align: "end",
      sortValue: (r) => r.supportedLanguages.length,
      cell: (r) => r.supportedLanguages.length,
    },
    {
      id: "currency",
      header: t("restaurants.list.columns.currency"),
      priority: "meta",
      sortValue: (r) => r.currency,
      cell: (r) => r.currency,
    },
  ];

  const remove = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/restaurants/${pendingDelete.id}`);
      notify.success(
        t("restaurants.list.deleted", { name: pendingDelete.name }),
      );
      setPendingDelete(null);
      reload();
    } catch (e) {
      notify.error(apiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const add = (
    <LinkButton
      to="/restaurants/create"
      variant="primary"
      icon={<PlusIcon className="size-5" aria-hidden="true" />}
    >
      {t("restaurants.list.add")}
    </LinkButton>
  );

  return (
    <>
      <PageHeader
        title={t("restaurants.list.title")}
        actions={data && data.length > 0 ? add : undefined}
      />
      <DataTable
        rows={data ?? []}
        columns={columns}
        getRowId={(r) => r.id}
        rowHref={(r) => `/restaurants/${r.id}`}
        rowLabel={(r) => r.name}
        rowActions={(r) => [
          {
            id: "open",
            label: t("list.open"),
            icon: ArrowTopRightOnSquareIcon,
            to: `/restaurants/${r.id}`,
          },
          {
            id: "edit",
            label: t("common:ui.edit"),
            icon: PencilSquareIcon,
            to: `/restaurants/edit/${r.id}`,
          },
          {
            id: "delete",
            label: t("common:ui.delete"),
            icon: TrashIcon,
            tone: "danger",
            separated: true,
            onSelect: () => setPendingDelete(r),
          },
        ]}
        state={state}
        onRetry={retry}
        caption={t("restaurants.list.title")}
        empty={{
          icon: BuildingStorefrontIcon,
          title: t("restaurants.list.emptyTitle"),
          body: t("restaurants.list.emptyBody"),
          action: add,
        }}
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={remove}
        busy={deleting}
        title={t("restaurants.list.deleteTitle", {
          name: pendingDelete?.name ?? "",
        })}
      >
        {t("restaurants.list.deleteBody")}
      </ConfirmDialog>
    </>
  );
};

export default RestaurantList;
